import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils/cn'

/** `panel` = the prototype's PanelEmpty (quiet, inside side panels); `page` = a screen-level empty state. */
const emptyVariants = cva('flex min-w-0 flex-col items-center justify-center text-center text-balance', {
  variants: {
    size: {
      panel: 'gap-1 px-4 py-7 text-fg-tertiary type-body',
      page: 'gap-4 px-6 py-14',
    },
  },
  defaultVariants: { size: 'panel' },
})

function Empty({ className, size, ...props }: React.ComponentProps<'div'> & VariantProps<typeof emptyVariants>) {
  return <div data-slot="empty" className={cn(emptyVariants({ size }), className)} {...props} />
}

function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="empty-header" className={cn('flex max-w-sm flex-col items-center gap-1', className)} {...props} />
  )
}

/** The dropzone-style icon tile (44px panel tile with a 22px accent glyph). */
function EmptyMedia({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-icon"
      className={cn(
        "mb-2 flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-panel text-fg-accent shadow-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5.5",
        className,
      )}
      {...props}
    />
  )
}

function EmptyTitle({ className, children, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2 data-slot="empty-title" className={cn('m-0 text-fg type-h2', className)} {...props}>
      {children}
    </h2>
  )
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return <p data-slot="empty-description" className={cn('m-0 text-fg-secondary type-body', className)} {...props} />
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="empty-content" className={cn('flex items-center gap-2', className)} {...props} />
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia }
